import React, { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { useSceneStore } from '../store'

const _box = new THREE.Box3()
const _edge = new THREE.Vector3()
const _center = new THREE.Vector3()
const _corner = new THREE.Vector3()

const CENTER_BAND = 0.18 // fraction of viewport height — fade in near vertical center
const BTN_HALF = 36 // px; ~72px button sits half on / half off the book edge
const MOBILE_MAX = 700

function isMobileViewport() {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_MAX
}

// Shared across both carousel play buttons so they don't fight over hover state
const hoverOwners = new Set()

function syncBookHovering() {
  useSceneStore.getState().actions.setBookHovering(hoverOwners.size > 0)
}

const PLAY_MARKUP = `
  <span class="carousel-play-btn__ring" aria-hidden="true"></span>
  <span class="carousel-play-btn__icon" aria-hidden="true"></span>
`

function projectToClient(v, camera, rect) {
  v.project(camera)
  return {
    x: (v.x * 0.5 + 0.5) * rect.width + rect.left,
    y: (-v.y * 0.5 + 0.5) * rect.height + rect.top,
  }
}

/**
 * Tracks a book group and mounts a real DOM play button (outside the R3F tree).
 * Follows the OS pointer while it is over the book's screen bounds — does not rely
 * on R3F pointer events (the DOM button would steal those).
 */
export default function CarouselPlayButton({
  bookRef,
  side = 'right',
  id,
  active = false,
  onAdvance,
}) {
  const { camera, gl } = useThree()
  const { setWordBalloonVisible } = useSceneStore((s) => s.actions)
  const btnRef = useRef(null)
  const onAdvanceRef = useRef(onAdvance)
  const mouse = useRef({ x: 0, y: 0 })
  const ownerId = id || side
  const wasOver = useRef(false)
  const revealed = useRef(false)
  const lastDock = useRef({ x: 0, y: 0, valid: false })
  // After a click-advance, keep following the pointer only while the new front
  // book is mid wrap-around flight — not while settled off the book.
  const wrapFollow = useRef(false)
  const state = useRef({
    x: 0,
    y: 0,
    opacity: 0,
    active,
  })

  onAdvanceRef.current = onAdvance
  state.current.active = active

  function setOverBook(over) {
    if (over === wasOver.current) return
    wasOver.current = over
    if (over) {
      hoverOwners.add(ownerId)
      // Hide balloon once on enter; cursor chrome is driven by bookHovering in AnimatedCursorX
      setWordBalloonVisible(false)
    } else {
      hoverOwners.delete(ownerId)
    }
    syncBookHovering()
  }

  // Create the DOM button once, outside the R3F reconciler
  useEffect(() => {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = `carousel-play-btn carousel-play-btn--${side}`
    btn.setAttribute('aria-label', 'Advance book slides')
    // Prevent AnimatedCursorX clickable listeners from treating this as a UI control
    btn.setAttribute('data-cursor-ignore', 'true')
    btn.innerHTML = PLAY_MARKUP
    btn.style.opacity = '0'
    btn.style.pointerEvents = 'none'
    btn.style.left = '0px'
    btn.style.top = '0px'

    const bounce = () => {
      btn.classList.remove('is-bouncing')
      void btn.offsetWidth
      btn.classList.add('is-bouncing')
    }

    // Always keep pointer-events: none so wheel/scroll reaches the page.
    // Handle clicks via window hit-testing instead.
    const handlePointerDown = (e) => {
      if (e.button != null && e.button !== 0) return
      if (state.current.opacity < 0.15 || !state.current.active) return

      if (wasOver.current) {
        // Book mesh receives the click and advances; only play the bounce here
        bounce()
        return
      }

      const r = btn.getBoundingClientRect()
      const hit =
        e.clientX >= r.left &&
        e.clientX <= r.right &&
        e.clientY >= r.top &&
        e.clientY <= r.bottom
      if (!hit) return

      bounce()
      onAdvanceRef.current?.(e)
    }

    const handleAnimEnd = (e) => {
      if (e.target !== btn.querySelector('.carousel-play-btn__ring')) return
      btn.classList.remove('is-bouncing')
    }
    window.addEventListener('pointerdown', handlePointerDown)
    btn.addEventListener('animationend', handleAnimEnd)
    document.body.appendChild(btn)
    btnRef.current = btn

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown)
      btn.removeEventListener('animationend', handleAnimEnd)
      btn.remove()
      btnRef.current = null
      hoverOwners.delete(ownerId)
      wasOver.current = false
      revealed.current = false
      lastDock.current.valid = false
      wrapFollow.current = false
      syncBookHovering()
    }
  }, [side, ownerId])

  // Track real pointer position — desktop only (mobile stays docked, no hover follow)
  useEffect(() => {
    if (isMobileViewport()) return
    const onMove = (e) => {
      mouse.current.x = e.clientX
      mouse.current.y = e.clientY
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    return () => window.removeEventListener('pointermove', onMove)
  }, [])

  useEffect(() => {
    if (!active) {
      setOverBook(false)
      revealed.current = false
      lastDock.current.valid = false
      wrapFollow.current = false
    }
  }, [active])

  useFrame((_, delta) => {
    const btn = btnRef.current
    const book = bookRef?.current
    if (!btn) return

    if (!state.current.active || !book) {
      revealed.current = false
      lastDock.current.valid = false
      wrapFollow.current = false
      state.current.opacity += (0 - state.current.opacity) * Math.min(1, delta * 10)
      btn.style.opacity = String(state.current.opacity)
      btn.style.pointerEvents = 'none'
      setOverBook(false)
      return
    }

    book.updateWorldMatrix(true, false)
    _box.setFromObject(book)
    if (_box.isEmpty()) return

    const rect = gl.domElement.getBoundingClientRect()

    _box.getCenter(_center)
    const midY = (_box.min.y + _box.max.y) * 0.5
    const midZ = (_box.min.z + _box.max.z) * 0.5
    if (side === 'right') {
      _edge.set(_box.max.x, midY, midZ)
    } else {
      _edge.set(_box.min.x, midY, midZ)
    }

    const centerScreen = projectToClient(_center.clone(), camera, rect)
    const edgeScreen = projectToClient(_edge.clone(), camera, rect)

    // Project AABB corners → screen bounds for hit-testing
    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity
    const { min, max } = _box
    for (let i = 0; i < 8; i++) {
      _corner.set(
        i & 1 ? max.x : min.x,
        i & 2 ? max.y : min.y,
        i & 4 ? max.z : min.z
      )
      const p = projectToClient(_corner, camera, rect)
      minX = Math.min(minX, p.x)
      maxX = Math.max(maxX, p.x)
      minY = Math.min(minY, p.y)
      maxY = Math.max(maxY, p.y)
    }

    const dist = Math.abs(centerScreen.y - (rect.top + rect.height * 0.5))
    const band = rect.height * CENTER_BAND
    const centerAmount = Math.max(0, 1 - dist / band)

    // Latch visible once the book reaches vertical center — stay at full opacity after
    const justRevealed = !revealed.current && centerAmount > 0.35
    if (centerAmount > 0.35) revealed.current = true

    const mx = mouse.current.x
    const my = mouse.current.y
    const overBook =
      mx >= minX && mx <= maxX && my >= minY && my <= maxY

    // Dock from the front book's edge. On wrap-around the new front book flies in from
    // z≈500 and its projection sweeps across the screen — ignore large dock jumps and
    // keep the last stable position until the book settles nearby again.
    const dockX = side === 'right' ? edgeScreen.x : edgeScreen.x - BTN_HALF
    const dockY = edgeScreen.y
    const bookOnScreen =
      edgeScreen.x > rect.left &&
      edgeScreen.x < rect.right &&
      edgeScreen.y > rect.top &&
      edgeScreen.y < rect.bottom &&
      maxX - minX > 20 &&
      maxY - minY > 20

    const DOCK_SNAP_PX = 90
    const mobile = isMobileViewport()
    const dockJump = lastDock.current.valid
      ? Math.hypot(dockX - lastDock.current.x, dockY - lastDock.current.y)
      : 0

    if (bookOnScreen) {
      if (!lastDock.current.valid || !revealed.current || justRevealed) {
        lastDock.current = { x: dockX, y: dockY, valid: true }
        if (justRevealed) {
          state.current.x = dockX
          state.current.y = dockY
        }
      } else if (dockJump < DOCK_SNAP_PX) {
        lastDock.current = { x: dockX, y: dockY, valid: true }
      } else if (!mobile && !wrapFollow.current) {
        // Desktop: large move while not wrapping (scroll / approach) — keep tracking
        lastDock.current = { x: dockX, y: dockY, valid: true }
      }
      // else: wrap-around teleport (or mobile) — keep last stable dock
    }

    // Follow the pointer only over the book (desktop). Mobile stays at the dock;
    // book hover tracking is disabled. During wrap-around flight (desktop, after a
    // click while hovering), keep following until the new front book settles.
    const overSettledBook =
      !mobile &&
      overBook &&
      revealed.current &&
      bookOnScreen &&
      dockJump < DOCK_SNAP_PX
    const inWrapFlight =
      !mobile &&
      wrapFollow.current &&
      revealed.current &&
      (!bookOnScreen || dockJump >= DOCK_SNAP_PX)

    let targetX
    let targetY

    if (overSettledBook) {
      wrapFollow.current = true
      targetX = mx
      targetY = my
    } else if (inWrapFlight) {
      targetX = mx
      targetY = my
    } else {
      wrapFollow.current = false
      if (lastDock.current.valid) {
        targetX = lastDock.current.x
        targetY = lastDock.current.y
      } else if (bookOnScreen) {
        targetX = dockX
        targetY = dockY
      } else {
        targetX = state.current.x
        targetY = state.current.y
      }
    }

    // Hide cursor chrome over the book (desktop only)
    setOverBook(mobile ? false : overSettledBook || inWrapFlight)

    const ease = 1 - Math.exp(-14 * delta)
    state.current.x += (targetX - state.current.x) * ease
    state.current.y += (targetY - state.current.y) * ease
    const targetOpacity = revealed.current ? 1 : 0
    state.current.opacity += (targetOpacity - state.current.opacity) * ease

    btn.style.left = `${state.current.x}px`
    btn.style.top = `${state.current.y}px`
    btn.style.opacity = String(state.current.opacity)
    // Never capture input — scrolling must reach the page underneath
    btn.style.pointerEvents = 'none'
  })

  return null
}
